import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import moment from 'moment';

import { StandardSwitch, SwitchSize } from '@components/library/switch';
import DateTimePicker from '@components/library/date_time_picker/BaseDateTimePicker';
import { bulkRefreshSections, pluralizeObject } from '@helpers/show_page_helpers';
import { updateAvailability } from '@api/v4/bulk_actions/assets';
import renderModal from '../modals/renderModal';

const defaultExpiration = moment().add(7, 'days').set({ hour: 23, minute: 59, second: 0 }).format('YYYY-MM-DD HH:mm');
const title = bfTranslate("An asset has external links.");
const textintro = bfTranslate("At least one of your selected assets has external links. ");
const text = `${textintro} ${bfTranslate('This will cause broken image links on sites that depend on the availability of these assets. Check the usage tab to see active external links before changing availability..\n\n')}`;
const sweetAlertOptions = {
  title,
  text,
  type: "warning",
  showCancelButton: true,
  cancelButtonText: bfTranslate("Cancel"),
  confirmButtonText: bfTranslate("Update"),
  closeOnConfirm: true,
};

const BulkAvailabilityModal = ({
  activeCdnTracers,
  closeModal,
  selectedAssetKeys,
}) => {
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [availabilityActive, setAvailabilityActive] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [expiresActive, setExpiresActive] = useState(false);
  const [expires, setExpires] = useState(null);

  const assetQtyObject = pluralizeObject(selectedAssetKeys);
  const assetCopy = assetQtyObject.plural ? bfTranslate('assets') : bfTranslate('asset');

  useEffect(() => (() => closeModal()), []);

  const sendAvailabilityUpdate = () => {
    updateAvailability({
      asset_keys: [...selectedAssetKeys], // convert Set to an array via spread operator
      availability_start: availability,
      availability_end: expires,
    })
      .then(({response, json}) => {
        bulkRefreshSections(selectedAssetKeys);
        closeModal();
        if (response.status == 202) {
          Notify.create({
            title: `${bfTranslate('Processing availability updates for')} ${assetQtyObject.qty} ${assetCopy}!`,
            body: bfTranslate('Updates will be available shortly, but may not yet be visible.'),
            type: 'success'
          });
        } else if (response.status == 200) {
          Notify.create({
            title: `${bfTranslate('Availability updated for')} ${assetQtyObject.qty} ${assetCopy}!`,
            type: 'success'
          });
        }
      })
      .catch(() => {
        setSubmitDisabled(false);
        Notify.create({
          title: bfTranslate('An error occurred when updating asset availability. Please try again.'),
          type: 'error'
        });
      });
  };

  const handleUpdate = () => {
    setSubmitDisabled(true);
    if ((availability || expires) && activeCdnTracers) {
      window.swal(sweetAlertOptions, (updateSelected) => {
        if (updateSelected) {
          sendAvailabilityUpdate();
        } else {
          setSubmitDisabled(false);
        }
      });
    } else {
      sendAvailabilityUpdate();
    }
  };

  useEffect(() => {
    if (!availabilityActive) setAvailability(null);
  }, [availabilityActive]);

  useEffect(() => {
    if (!expiresActive) setExpires(null);
  }, [expiresActive]);

  return (
    <div className="modal-content-wrapper bulk-availability-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-clock icon" aria-hidden="true" />
        <h3 className="modal-title">{bfTranslate('Schedule availability for')}
          <span className="bold-text">
            {selectedAssetKeys.size} {assetCopy}
          </span>
        </h3>
        <button
          className="close-button"
          onClick={closeModal}
          type="button"
        >
          <span className="bff-close" />
        </button>
      </div>
      <div className="modal-content-wrapper__body">
        <div className="toggle-row">
          <div className="toggle-container">
            <h4 className="toggle-title">{bfTranslate('Enable Availability Date')}</h4>
            <StandardSwitch
              className="t-enable-availability"
              isChecked={availabilityActive}
              onChange={() => setAvailabilityActive((prevState) => (!prevState))}
              size={SwitchSize.Large}
            />
          </div>
          <CSSTransition
            in={availabilityActive}
            timeout={300}
            unmountOnExit
          >
            <div className="date-time-picker-transition">
              <DateTimePicker
                className="availability-picker"
                onChange={(timestamp) => setAvailability(timestamp)}
                timestamp={availability}
              />
            </div>
          </CSSTransition>
        </div>
        <div className="toggle-row">
          <div className="toggle-container">
            <h4 className="toggle-title">{bfTranslate('Enable Expiration Date')}</h4>
            <StandardSwitch
              className="t-enable-expiration"
              isChecked={expiresActive}
              onChange={() => setExpiresActive((prevState) => {
                if (!prevState) setExpires(defaultExpiration);
                return !prevState;
              })}
              size={SwitchSize.Large}
            />
          </div>
          <CSSTransition
            in={expiresActive}
            timeout={300}
            unmountOnExit
          >
            <div className="date-time-picker-transition">
              <DateTimePicker
                className="expiration-picker"
                onChange={(timestamp) => setExpires(timestamp)}
                timestamp={expires}
              />
            </div>
          </CSSTransition>
        </div>
        <div className="button-container share-modal-button-container">
          <button
            className="button primary sm t-confirm-modal"
            disabled={submitDisabled}
            onClick={handleUpdate}
            type="button"
          >
            {bfTranslate('Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

BulkAvailabilityModal.propTypes = {
  activeCdnTracers: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

const ModalComponent = renderModal(BulkAvailabilityModal, 'BulkAvailabilityModal');
export default ModalComponent;
